import type { AgentMessagePart, AccessGroup, AccessTableEntry } from '../../types/agent-messages';
import type { ParserContext, ParserResult } from './types';

const ACCESS_CHECK_TOOLS = new Set([
  'check_data_access',
  'check_access',
  'fintellaCheckAccess',
  'andesCheckAccess',
]);

const QUERY_EXEC_TOOLS = new Set([
  'sql_execute',
  'execute_query',
  'sqlExecuteSample',
]);

/**
 * Inspects tool step outputs to produce rich parts:
 *  - `check_data_access` / `check_access` -> `accessStatus` part
 *  - `sql_execute` / `execute_query`       -> `outputPreview` part
 *  - Any tool with `status: 'error'`       -> `notification` part (error variant)
 */
export function toolResultParser(ctx: ParserContext): ParserResult {
  const parts: AgentMessagePart[] = [];

  for (const step of ctx.toolSteps) {
    if (step.status === 'error' && step.output) {
      parts.push({
        type: 'notification',
        variant: 'error',
        title: `${step.displayName || step.toolName} failed`,
        description: typeof step.output === 'string' ? step.output : JSON.stringify(step.output),
      });
      continue;
    }

    if (ACCESS_CHECK_TOOLS.has(step.toolName) && step.output) {
      const accessPart = tryParseAccessStatus(step.output);
      if (accessPart) {
        parts.push(accessPart);
        continue;
      }
    }

    if (QUERY_EXEC_TOOLS.has(step.toolName) && step.output) {
      const previewPart = tryParseOutputPreview(step.output);
      if (previewPart) {
        parts.push(previewPart);
      }
    }
  }

  return { parts, remainingText: ctx.text };
}

function tryParseAccessStatus(output: unknown): AgentMessagePart | null {
  if (!output || typeof output !== 'object') return null;

  const data = output as Record<string, unknown>;

  if (Array.isArray(data.groups)) {
    const groups: AccessGroup[] = (data.groups as any[]).map((g) => ({
      systemName: g.system_name || g.systemName || 'Unknown',
      tables: Array.isArray(g.tables)
        ? g.tables.map(
            (t: any): AccessTableEntry => ({
              name: t.name || t.table_name || '',
              status: mapAccessStatus(t.status),
              action: t.action_url || t.action,
              owner: t.owner,
              classification: t.classification,
            }),
          )
        : [],
    }));

    const allTables = groups.flatMap((g) => g.tables);
    const granted = allTables.filter((t) => t.status === 'granted').length;
    const summary = `${granted} of ${allTables.length} tables accessible`;

    return { type: 'accessStatus', groups, summary };
  }

  return null;
}

function mapAccessStatus(raw: unknown): 'granted' | 'denied' | 'pending' {
  const s = String(raw).toLowerCase();
  if (s === 'granted' || s === 'approved' || s === 'true') return 'granted';
  if (s === 'pending' || s === 'in_progress') return 'pending';
  return 'denied';
}

function tryParseOutputPreview(output: unknown): AgentMessagePart | null {
  if (!output || typeof output !== 'object') return null;

  const data = output as Record<string, unknown>;

  if (Array.isArray(data.columns) && Array.isArray(data.rows)) {
    return {
      type: 'outputPreview',
      columns: data.columns as string[],
      rows: data.rows as unknown[][],
      totalEstimate:
        typeof data.totalEstimate === 'number'
          ? data.totalEstimate
          : (data.rows as unknown[][]).length,
      queryTime: data.queryTime != null ? String(data.queryTime) : undefined,
    };
  }

  if (Array.isArray(data.results) && (data.results as any[]).length > 0) {
    const firstRow = (data.results as any[])[0];
    const columns = Object.keys(firstRow);
    const rows = (data.results as any[]).map((r) =>
      columns.map((c) => r[c]),
    );
    return {
      type: 'outputPreview',
      columns,
      rows,
      totalEstimate: rows.length,
    };
  }

  return null;
}
